import { api } from './api';

export async function listQueue() {
  return await api.get('/api/fax-inbox/queue');
}

export async function approveItem(groupId) {
  return await api.post(`/api/fax-inbox/${groupId}/approve`, {});
}

export async function rejectItem(groupId, reason) {
  return await api.post(`/api/fax-inbox/${groupId}/reject`, { reason });
}

// Re-run a parked group through triage under the practice's CURRENT settings
// (no signing) — newly-enabled automation toggles take effect, so e.g. a parked
// signature_request can flip to a form-fill card. Returns { ok:true, result } on
// success; throws via api.post on 409 (group no longer in the Elation inbox) /
// 500 { error }. The caller should reload() after to see the new card/disposition.
export async function reprocessItem(groupId) {
  return await api.post(`/api/fax-inbox/${groupId}/reprocess`, {});
}

// Approve a queued rx_refill_pending item: the Task 4 approve route special-
// cases suggested_classification === 'rx_refill_pending' and builds the unsigned
// eRx draft from the chosen `selection` (+ optional physicianId). Returns
// { ok, draftId } on a 2xx; throws via api.post (400 no medication / 409 no
// physician / 500) with .message = server { error }, in which case the row
// stays in review.
export async function approveRefill(groupId, body) {
  return await api.post(`/api/fax-inbox/${groupId}/approve`, body);
}

export async function dismissFiledItem(groupId) {
  return await api.post(`/api/fax-inbox/${groupId}/dismiss`, {});
}

export async function dismissAllFiled(days = 7) {
  return await api.post(`/api/fax-inbox/recent-filings/dismiss-all`, { days });
}

export async function runWorker() {
  return await api.post('/api/fax-inbox/run', {});
}

export async function getMetrics() {
  return await api.get('/api/fax-inbox/metrics');
}

export async function recentFilings(days = 7) {
  return await api.get(`/api/fax-inbox/recent-filings?days=${days}`);
}

export function imageProxyUrl(groupId, imageId) {
  return `/api/fax-inbox/image/${groupId}/${imageId}`;
}

export async function getMode() {
  return await api.get('/api/fax-inbox/mode');
}

// Apply a PARTIAL automation patch — any subset of the 5 boolean capability
// flags (autoFileMisc, autoFileStructured, draftRefills, resolveRefills,
// signForms). The backend seeds untouched flags and returns { ok, automation }.
export async function setAutomation(patch) {
  return await api.put('/api/fax-inbox/automation', patch);
}

// Prefill defaults editor: GET returns { synced:{provider,office}, overrides:{provider,office}, can_edit }
// (Elation-synced values + the practice's sparse overrides). setPrefillDefaults replaces the
// overrides with a sparse object — only the items you changed; blanks follow Elation.
export async function getPrefillDefaults() {
  return await api.get('/api/fax-inbox/prefill-defaults');
}
export async function setPrefillDefaults(overrides) {
  return await api.put('/api/fax-inbox/prefill-defaults', overrides);
}

// Save an adjusted signature-placement for a queued signature_request item.
// The Task 8 approve route stamps this STORED placement, so dragging a box
// here changes where the signature lands. The backend validates strictly
// (non-empty array; integer page>=1; signatureBox {x,y,w,h} numeric in [0,1];
// dateBox null or same range) → keep coords clamped to [0,1] so this never 400s.
export async function setSignaturePlacement(groupId, placement) {
  return await api.post(`/api/fax-inbox/${groupId}/signature-placement`, { placement });
}

// Render the EXACT stamped PDF Approve & sign would file/fax, but send NOTHING —
// the signature-card preview. Returns { pdfBase64, returnFax, signerOk,
// signerReason } (or { error, signerOk, signerReason } on a missing signer/
// placement/pages/render failure — still HTTP 200, so don't throw on it).
export async function getSignPreview(groupId) {
  return await api.post(`/api/fax-inbox/${groupId}/sign-preview`, {});
}

// Admin-only: add a form-type key to the sign-forms allowlist so this subtype
// auto-signs going forward. Returns { ok, allowlist }.
export async function allowlistFormType(subtypeKey) {
  return await api.post('/api/fax-inbox/sign-forms/allowlist', { subtypeKey });
}

// Admin-only: load the lab_coverage_codes reference table from a normalized CSV
// (produced by scripts/lab-coverage/parse-cms-xlsx.js). The CSV is large
// (~42 MB), so the admin uploads it ALREADY gzipped (`gzip lab-coverage.csv`)
// and we send the gzip bytes base64-encoded in the JSON body — gzip ≈ 3 MB,
// base64 ≈ 4 MB, well under the 30 MB express.json limit. The server gunzips,
// parses, and ingests (replace-by-source). Returns
// { ok:true, inserted, sourceFile, ncds, cpts }; throws via api.post on a 400
// (missing/invalid payload, no valid rows) / 500.
export async function seedLabCoverage(csvGzipBase64) {
  return await api.post('/api/fax-inbox/lab-coverage/seed', { csvGzipBase64 });
}

// Admin-only: load the lab_vendor_coverage table (a lab's published decision-support
// ICD-10 aide, Northwell first) from a gzipped CSV produced by
// `scripts/lab-coverage/import-northwell-coverage.js --out-csv`. Same gzip+base64
// upload as seedLabCoverage; the file is tiny (~2k rows). Server replace-by-vendor.
// Returns { ok:true, inserted, vendorKey, sections }.
export async function seedVendorCoverage(csvGzipBase64) {
  return await api.post('/api/fax-inbox/vendor-coverage/seed', { csvGzipBase64 });
}

// ── Form-Fill (Phase 4) ─────────────────────────────────────────────────────
// A "fillable_form" queue item is an extracted form whose fields the clinician
// confirms one by one (pre-filled with chart suggestions), then signs +
// faxes back. The server is the source of truth: every /answer persists +
// server-validates the value (sets confirmed:true), and /complete re-guards
// (an incomplete form 409s) — so closing mid-fill is safe + resumable.

// GET the form definition + saved answers. Returns
// { fields:[{id,page,label,fieldType,options?,answerBox,unit?}],
//   answers:{[id]:{value,confirmed,source}}, signaturePlacement,
//   subtypeLabel, imageIds:[<page-ordered image ids>] }.
export async function getForm(groupId) {
  return await api.get(`/api/fax-forms/${groupId}`);
}

// Confirm one field. The server validates per fieldType and, on a 2xx, marks
// the field confirmed:true and returns a PATCH: only the CHANGED entry
// ({ ok:true, answers: { [fieldId]: entry } }) — the persist is a per-key
// atomic jsonb_set so parallel saves for different fields compose, and a
// patch response can never arrive stale and clobber a sibling field in the
// client's spread-merge. On a bad value it throws via api.post (400 { error },
// .message = the server error) — the field stays unconfirmed.
export async function answerField(groupId, fieldId, value) {
  return await api.post(`/api/fax-forms/${groupId}/answer`, { fieldId, value });
}

// Apply a reviewer placement correction to one field (confirm / pick a detected
// row / nudge) and mark its position confirmed. Returns { ok:true, fields, answers }.
export async function updatePlacement(groupId, fieldId, action) {
  return await api.post(`/api/fax-forms/${groupId}/placement`, { fieldId, action });
}

// Best-effort normalize free text into a field value ({ value }). NOT
// persisted — the client confirms the returned value via answerField.
export async function parseField(groupId, fieldId, freeText) {
  return await api.post(`/api/fax-forms/${groupId}/parse`, { fieldId, freeText });
}

// Stamp-queue batch decision: items = [{ id, action:'stamp'|'skip', value? }]
// where id is a field id or a 'sig:<page>'/'date:<page>' pseudo-id.
// stamp = confirm value + position in ONE write; skip = explicit no-ink decision.
// ATOMIC on the server: any invalid item → 400 { items:[{id,error}] }, nothing
// applied. Returns { ok:true, answers:{ [id]: entry } } (changed entries only —
// spread-merge like answerField).
export async function stampFields(groupId, items) {
  return await api.post(`/api/fax-forms/${groupId}/stamp`, { items });
}

// Provider-agnostic field decision-support for the rail. Returns the generic
// field-context contract portion: { options:[{value,label,recommended?,onChart?,
// status?,source}], search:{enabled,placeholder?}, coverageChecked,
// contextBlocks?:[{kind,severity,title,body,citation?}] }. `q` is the optional
// search term (omit / '' for the upfront chart-candidates + AI-advisory call).
// Fail-soft on the server → empty options; the rail still shows persisted
// source/value/rationale/confidence.
export async function fetchFieldOptions(groupId, fieldId, q) {
  const qs = `fieldId=${encodeURIComponent(fieldId)}${q ? `&q=${encodeURIComponent(q)}` : ''}`;
  return await api.get(`/api/fax-forms/${groupId}/field-options?${qs}`);
}

// Render the EXACT filled PDF /complete would send, but file/fax NOTHING — for
// the Review-step preview. Reflects the same {skipSignature, skipDate} toggles so
// the preview matches them. Returns
// { pdfBase64, returnFax, signerOk, signerReason } (or { error } on a
// page-fetch/render failure — still HTTP 200, so don't throw on it). Read-only:
// never 409s on a missing signer / incomplete fields.
export async function getFormPreview(groupId, opts = {}) {
  return await api.post(`/api/fax-forms/${groupId}/preview`, opts);
}

// Render + sign + fax the completed form back. Returns
// { ok:true, reportId, letterId, sent } on success; throws via api.post on a
// 409 (e.g. 'not all fields confirmed' / signer reason) or 500 — in which case
// nothing was sent and the row stays in review.
//
// opts (all default OFF → today's behavior):
//   allowIncomplete — sign/fax with some fields left blank (audited);
//   skipSignature   — don't stamp a signature (none needed / none on file);
//   skipDate        — don't stamp the sign-date.
export async function completeForm(groupId, opts = {}) {
  return await api.post(`/api/fax-forms/${groupId}/complete`, opts);
}
